import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import {
  useAppActions,
  useAppIds,
  useAppStore,
  useHasHydrated,
} from "@/stores/appStore";
import { api } from "../api";
import { ExplorerApp } from "./ExplorerApp";
import { Footer } from "./components/Footer";
import { Header } from "./components/Header";
import { AppInstanceProvider } from "./providers/app-instance-provider";
import { useMapActions } from "./stores/mapStore";
import { Config, driver, DriveStep } from "driver.js";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from "./components/ui/alert-dialog";

const chartSettingStep: DriveStep[] = [
  {
    element: ".chart-setting-icon",
    popover: {
      description: `This button opens the chart editor, which allows you to change how the data are displayed. Note this feature is only available for the trend charts.`,
      side: "right",
      align: "start",
    },
  },
  {
    element: ".chart-reset-icon",
    popover: {
      description: `The reset button will restore the default settings.`,
      side: "right",
      align: "start",
    },
  },
];

const userJourneySteps: DriveStep[] = [
  {
    popover: {
      description: `<div class="flex flex-col items-center">Welcome to Data Explorer Arizona!  <img style="width:100%; margin-top:15px" src="/images/data-explorer-full-color.png" alt="AZ EPHT"> <br> Let's explore the dashboard features. <br><br> Press Next or use keyboard arrow keys to explore the dashboard.You can further filter your query using the Advanced Filters.</div>`,
      side: "right",
      align: "start",
    },
  },
  {
    element: "#queryPanelContainer",
    popover: {
      description: `Let's start in the query panel. Use the dropdowns to select which data you would like to view. Data is categorized into topics, such as <b>Death</b> or <b>Birth</b>. From there, you can select specific content areas, indicators, and measures`,
      side: "right",
      align: "start",
    },
  },
  {
    element: ".mapPanelContainer",
    popover: {
      description: `Here is the map panel. When you change your query and click the run query button, the map is automatically updated.`,
      side: "right",
      align: "start",
    },
  },
  {
    element: "#map-zoom-control",
    popover: {
      description: `Zoom in or out on the map using the controls here.`,
      side: "right",
      align: "start",
    },
  },
  {
    element: "#map-panel-print",
    popover: {
      description: `Use the print icon to download a pdf of the current map view`,
      side: "bottom",
      align: "center",
    },
  },
  {
    element: ".mainMap",
    popover: {
      description: `Click on a county to select it and data from that county will be highlighted in the table and chart.`,
      side: "right",
      align: "start",
    },
  },
  {
    element: ".tablePanelContainer",
    popover: {
      description: `Here is the table of data for this query.`,
      side: "right",
      align: "start",
    },
  },
  {
    element: ".tablePanelContainer",
    popover: {
      description: `Click on rows to highlight that geography in the map, and add/highlight it in the chart. To sort the data, click on the headers.`,
      side: "right",
      align: "start",
    },
  },
  {
    element: ".talePanelDownloadBtn",
    popover: {
      description: `The <b>Download Data</b> button allows you to download the data as shown in the table.`,
      side: "right",
      align: "start",
    },
  },
  {
    element: ".includeChart",
    popover: {
      description: `The <b>Include Chart</b> selection allows you to download the chart while downloading the data.`,
      side: "right",
      align: "start",
    },
  },
  {
    element: ".tab-panel-2",
    popover: {
      description: `Click on Advanced Download to download the data as per your selected filter`,
      side: "right",
      align: "start",
    },
  },
  {
    element: ".mapSettingLayerListContainer",
    popover: {
      description: `<b>Map Settings</b>, and <b>Layer List</b> are located here.`,
      side: "right",
      align: "start",
    },
  },
  {
    element: ".mapSettingContainer",
    popover: {
      description: `The <b>Map Settings</b> panel allows you to change how the data are grouped and color-coded on the map, table, and chart. BaseMap refers to the type of background map.`,
      side: "right",
      align: "start",
    },
  },
  {
    element: ".chartsContainer",
    popover: {
      description: `Here are the charts. The charts are viewable with your selected features.`,
      side: "right",
      align: "start",
    },
  },
  {
    element: ".chartsTabsContainer",
    popover: {
      description: `These tabs change the chart type. You can view an individual data series as a column chart or look at trends over time.`,
      side: "right",
      align: "start",
    },
  },
  {
    element: ".chart-tab-1",
    popover: {
      description: `The darker color shows your current active chart.`,
      side: "right",
      align: "start",
    },
  },
  {
    element: ".chart-tab-2",
    popover: {
      description: `Users can switch to the <b>Column Chart</b>, and the chart below will update accordingly.`,
      side: "right",
      align: "start",
    },
  },
  {
    element: ".chart-tab-3",
    popover: {
      description: `Click the Advanced Charts tab to view charts by your Advanced Filter selections.`,
      side: "right",
      align: "start",
    },
  },
  {
    element: ".mapLegendTabContainer",
    popover: {
      description: `Here is the <b>Legend</b>.`,
      side: "right",
      align: "start",
    },
  },
  {
    element: ".userGuideBtn",
    popover: {
      description: `Click the button to download the User Guide.`,
      side: "right",
      align: "start",
    },
  },
  {
    element: ".addMapBtn",
    popover: {
      description: `Click the button to add another map in split view.`,
      side: "right",
      align: "start",
    },
  },
  {
    element: ".quickLinkBtn",
    popover: {
      description: `Click here for other quick links.`,
      side: "right",
      align: "start",
    },
  },
  {
    element: ".aboutBtn",
    popover: {
      description: `Learn more about the AZ Public Health Data Portal.`,
      side: "right",
      align: "start",
    },
  },
  {
    element: ".dataStoriesBtn",
    popover: {
      description: `Know more about Data Stories.`,
      side: "right",
      align: "start",
    },
  },
  {
    element: ".feedbackBtn",
    popover: {
      description: `Share your feedback.`,
      side: "right",
      align: "start",
    },
  },
  {
    popover: {
      description: `Alright, ready to dive in?`,
      side: "right",
      align: "start",
    },
  },
];

const queryHelpSteps: DriveStep[] = [
  {
    element: "#queryPanelContainer",
    popover: {
      description: `Use the dropdown menus to select the data you would like to view. Data is categorized by <b>Topic</b>, <b>Geography Type</b>, <b>Content Area</b>, <b>Indicator</b>, <b>Measure</b>, and <b>Year</b>.`,
      side: "right",
      align: "start",
    },
  },
  {
    element: ".runQueryBtn",
    popover: {
      description: `Once all the required filters are selected, click <b>Run Query</b> to visualize the data on a map, table, and charts.`,
      side: "right",
      align: "start",
    },
  },
  {
    element: ".mapPanelContainer",
    popover: {
      description: `Here is the map panel. The map is automatically updated after Run Query is clicked`,
      side: "right",
      align: "start",
    },
  },
  {
    element: ".tablePanelContainer",
    popover: {
      description: `Here is the table of data for this query.`,
      side: "right",
      align: "start",
    },
  },
  {
    element: ".tab-panel-2",
    popover: {
      description: `Click on Advanced Download to download the data as per your selected filter`,
      side: "right",
      align: "start",
    },
  },
  {
    element: ".chartsContainer",
    popover: {
      description: `Here are the charts. The charts are viewable with your selected features.`,
      side: "right",
      align: "start",
    },
  },
  {
    element: ".chart-tab-3",
    popover: {
      description: `Click the Advanced Charts tab to view charts by your Advanced Filter selections.`,
      side: "right",
      align: "start",
    },
  },
  {
    element: ".queryPanelSearchIcon",
    popover: {
      description: `Use the search button to search for <b>Content Areas</b>, <b>Indicators</b> or <b>Measures</b>.`,
      side: "right",
      align: "start",
    },
  },
  {
    element: ".queryPanelResetIcon",
    popover: {
      description: `Use the reset button to reset the query and start fresh.`,
      side: "right",
      align: "start",
    },
  },
  {
    element: ".queryPanelCopyIcon",
    popover: {
      description: `Use the copy button to copy your selected query and share the URL with others.`,
      side: "right",
      align: "start",
    },
  },
];

const advancedFilteStep: DriveStep[] = [
  {
    element: ".advancedFilterContainer",
    popover: {
      description: `Use the advanced filter section to further filter your data more.`,
      side: "right",
      align: "start",
    },
  },
];

export default function InstanceApp() {
  const tourKeys = {
    completed: "isTourCompleted",
    aborted: "isTourAborted",
  };
  const apps = useAppIds();
  const queryHelpDriverRef = useRef<ReturnType<typeof driver> | null>(null);
  const driverRef = useRef<ReturnType<typeof driver> | null>(null);
  const [errorPopupOpen, setErrorPopupOpen] = useState(false);
  const [errorPopupMessage, setErrorPopupMessage] = useState("");
  const [isTourCompleted, setIsTourCompleted] = useState(
    localStorage.getItem(tourKeys.completed) === "true" ? true : false,
  );
  const [isTourAborted, setIsTourAborted] = useState(
    localStorage.getItem(tourKeys.aborted) === "true" ? true : false,
  );
  const hasHydrated = useHasHydrated();
  const {
    createApp,
    setServiceUrl,
    setItemId,
    removeApp,
    setIsTourInProgress,
    copyInstanceSetting: copyAppSettings,
  } = useAppActions();
  const { createMapSetting, copyInstanceSetting: copyMapSettings } =
    useMapActions();

  const handleCopyContentClick = useCallback(() => {
    if (apps?.length > 1) {
      copyAppSettings(apps?.[0], apps?.[1]);
      copyMapSettings(apps?.[0], apps?.[1]);
    }
  }, [apps, copyAppSettings, copyMapSettings]);

  const handleDownloadUserGuide = useCallback(() => {
    const link = document.createElement("a");

    link.href = "/assets/user_journey_guide.pdf";
    link.download = "user_journey_guide.pdf";

    document.body.appendChild(link);
    link.click();
    link.remove();
  }, []);

  const handleIsTourInProgress = useCallback((value: boolean) => {
    const appId = useAppStore.getState().apps?.[0];
    setIsTourInProgress(appId, value);
  }, []);

  const startTour = useCallback((checkAbortTour = true) => {
    if (isTourAborted && checkAbortTour) return;

    let steps: DriveStep[] = [...userJourneySteps];
    const ele = document.querySelector(".chart-setting-icon");
    if (ele) {
      steps = [...steps?.slice(0, 18), ...chartSettingStep, ...steps.slice(18)];
    }
    driverRef.current?.setSteps(steps);
    handleIsTourInProgress(true);
    driverRef?.current?.drive();
  }, [handleIsTourInProgress]);

  const onQueryHelpClick = useCallback(() => {
    const ele = document.querySelector(".advancedFilterContainer");
    let steps: DriveStep[] = [...queryHelpSteps];
    if (ele) {
      steps = [...steps?.slice(0, 1), ...advancedFilteStep, ...steps?.slice(1)];
    }

    queryHelpDriverRef?.current?.setSteps(steps);
    handleIsTourInProgress(true);
    queryHelpDriverRef?.current?.drive();
  }, [handleIsTourInProgress]);

  useEffect(() => {
    useAppStore.persist.rehydrate();

    const option: Config = {
      showProgress: true,
      smoothScroll: true,
      allowScroll: true,
      showButtons: ["previous", "next"],
      nextBtnText: "Next",
      prevBtnText: "Back",
      doneBtnText: "Done",
      onHighlightStarted: (element, step, { driver }) => {
        if (!element) return;
        element.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });

        if (element.scrollHeight > element.clientHeight) {
          element.scrollTop = 0;
        }

        setTimeout(() => {
          driver.refresh();
        }, 400);
      },
    };

    driverRef.current = driver({
      ...option,
      allowClose: false,
      onDoneClick: () => {
        localStorage.setItem(tourKeys.completed, "true");
        setIsTourCompleted(true);
        handleIsTourInProgress(false);
        driverRef?.current?.destroy();
      },
      onPopoverRender: (popover, { driver }) => {
        const skipBtn = document.createElement("button");
        skipBtn.innerText = "Explore Data";
        skipBtn.className = "driver-popover-footer-btn";
        skipBtn.addEventListener("click", () => {
          localStorage.setItem(tourKeys.aborted, "true");
          setIsTourAborted(true);
          handleIsTourInProgress(false);
          driver.destroy();
        });
        popover.footerButtons.prepend(skipBtn);
      },
      steps: userJourneySteps,
    });

    queryHelpDriverRef.current = driver({
      ...option,
      onPopoverRender: (popover, { driver }) => {
        const skipBtn = document.createElement("button");
        skipBtn.innerText = "Explore Data";
        skipBtn.className = "driver-popover-footer-btn";
        skipBtn.addEventListener("click", () => {
          localStorage.setItem(tourKeys.aborted, "true");
          setIsTourAborted(true);
          handleIsTourInProgress(false);
          driver.destroy();
        });
        popover.footerButtons.prepend(skipBtn);
      },
      steps: queryHelpSteps,
    });

    return () => {
      handleIsTourInProgress(false);
      driverRef?.current?.destroy();
      queryHelpDriverRef?.current?.destroy();
    };
  }, [handleIsTourInProgress]);

  useEffect(() => {
    if (!isTourCompleted) {
        startTour();
    }
  }, [isTourCompleted, startTour]);

  useEffect(() => {
    if (!hasHydrated) return;
    if (useAppStore.getState().apps.length === 0) {
      const appId = crypto.randomUUID();
      createApp(appId);
    }
  }, [hasHydrated]); // eslint-disable-line react-hooks/exhaustive-deps

  useLayoutEffect(() => {
    async function bootstrap() {
      const resp = await api.getEsriConfig();
      setServiceUrl(resp.data.service);
      setItemId(resp.data.itemId);
    }
    bootstrap();
  }, []);

  if (!hasHydrated || !apps.length) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <p className="text-sm text-muted-foreground">Loading…</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full h-screen">
      <Header
        isSplitView={apps.length > 1}
        isTourCompleted={isTourCompleted}
        onToggleSplitView={
          apps.length > 1
            ? () => removeApp(apps[1])
            : () => {
                const appId = crypto.randomUUID();
                createMapSetting(apps?.[0], appId);
                createApp(appId);
              }
        }
        onStartTourClick={() => startTour(false)}
        handleDownloadUserGuide={handleDownloadUserGuide}
        onCopyContentClick={handleCopyContentClick}
      />

      <div className="flex flex-1 w-full min-h-0">
        {apps.map((appId) => (
          <div
            key={appId}
            className="flex flex-col flex-1 h-full min-h-0 min-w-0"
          >
            <AppInstanceProvider appId={appId}>
              <ExplorerApp
                view={apps.length > 1 ? "shared" : "independent"}
                onQueryHelpClick={onQueryHelpClick}
              />
            </AppInstanceProvider>
          </div>
        ))}
      </div>

      <Footer />

      <AlertDialog open={errorPopupOpen} onOpenChange={setErrorPopupOpen}>
        <AlertDialogContent size="default">
          <AlertDialogHeader>
            <AlertDialogTitle>{"Error"}</AlertDialogTitle>
            <AlertDialogDescription>{errorPopupMessage}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setErrorPopupOpen(false)}>
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
